import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, IdCard, CheckCircle2, ArrowRight, SkipForward } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * FirstLoginWizard — single-step welcome modal that appears once when a new
 * advisor (typically arriving from BEplan SSO) hasn't yet set their vizitka
 * slug. Lets them either personalise the slug or skip with a soft nudge.
 *
 * Triggers:
 *   - user.first_login_completed !== true  (server-managed flag)
 *   AND
 *   - user fetched /api/profile/me has no vizitka_slug, OR has the
 *     auto-generated one we set on SSO callback (so the wizard always shows
 *     for fresh accounts).
 *
 * Once completed/skipped, we POST /api/profile/onboarding/complete to mark
 * the flag in DB so the modal never reappears for this user.
 */
export default function FirstLoginWizard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState(null);
  const [slug, setSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Only show for advisors / managers — admin already set up
    if (user.role === "admin") return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/api/profile/me");
        if (cancelled) return;
        const data = r.data || {};
        // Skip wizard if user already completed it OR already has a slug
        // (slug was set manually in Settings → Vizitka or auto-generated by SSO).
        if (data.first_login_completed) return;
        if (data.profile?.vizitka_slug) {
          // Slug already configured → silently mark onboarding as done so the
          // wizard never bothers them again.
          api.post("/api/profile/onboarding/complete", {}).catch(() => {});
          return;
        }
        setMe(data);
        setSlug((data.profile?.vizitka_slug || "").toLowerCase());
        setOpen(true);
      } catch {
        // silent — wizard is non-critical
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!open) return null;

  const cleanSlug = (slug || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);

  const finish = async (mode /* "save" | "skip" */) => {
    setSaving(true);
    try {
      if (mode === "save" && cleanSlug && cleanSlug !== me?.profile?.vizitka_slug) {
        await api.post("/api/profile/me", { profile: { ...(me.profile || {}), vizitka_slug: cleanSlug } });
      }
      await api.post("/api/profile/onboarding/complete", {}).catch(() => {});
      setDone(true);
      if (mode === "save") {
        toast.success(t("wizard.ulozeno", "Vizitka uložena!"));
      }
      // Auto-close after 1.2s
      setTimeout(() => setOpen(false), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wizard.chyba", "Uložení slugu selhalo. Můžeš to zkusit znovu v Nastavení → Vizitka."));
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = cleanSlug ? `${window.location.origin}/v/${cleanSlug}` : `${window.location.origin}/v/<tvoje-jmeno>`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
          data-testid="first-login-wizard"
        >
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.96 }}
            transition={{ type: "spring", damping: 22, stiffness: 220 }}
            className="bg-gradient-to-br from-[#0D1B3E] via-[#11214E] to-[#0D1B3E] border border-white/10 rounded-3xl shadow-[0_30px_100px_rgba(0,0,0,0.6)] w-full max-w-lg p-7 md:p-9 relative overflow-hidden"
          >
            {/* Decorative glow */}
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />

            {!done ? (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                    <GraduationCap size={20} className="text-emerald-300" />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.3em] text-emerald-300">{t("wizard.vitej", "Vítej v BEplan University")}</span>
                </div>

                <h2 className="text-white text-2xl md:text-3xl leading-tight mb-3" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>
                  {t("wizard.nadpis", "Nastav si {jmeno} digitální vizitku")
                    .replace("{jmeno}", (me?.name || "").split(" ")[0] || t("wizard.zastupneJmeno", "svoji"))}
                </h2>
                <p className="text-[#A0ABC0] text-sm leading-relaxed mb-6">
                  {t("wizard.popisA", "Vizitka je tvoje veřejná stránka — klienti odsud zarezervují schůzku, zavolají, najdou tě na LinkedIn.")}{" "}
                  <span className="text-white">{t("wizard.popisZvyraznene", "Zabere to 30 vteřin")}</span>{" "}
                  {t("wizard.popisB", "a pak ti začnou fungovat všechny Leadovač odkazy i karta doporučení.")}
                </p>

                <label className="block text-[11px] uppercase tracking-[0.22em] text-[#A0ABC0] mb-2">
                  {t("wizard.slugLabel", "Tvoje URL adresa (slug)")}
                </label>
                <div className="flex items-stretch bg-black/40 border border-white/10 rounded-xl overflow-hidden focus-within:border-emerald-500/60 transition-colors">
                  <span className="px-3 py-3 text-[#64748B] text-sm font-mono whitespace-nowrap select-none">
                    beplanuniversity.cz/v/
                  </span>
                  <input
                    autoFocus
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="jan-novak"
                    maxLength={40}
                    data-testid="wizard-slug-input"
                    className="flex-1 bg-transparent py-3 pr-3 text-white text-sm focus:outline-none placeholder:text-[#445]"
                  />
                </div>
                <p className="text-[10px] text-[#64748B] mt-2 mb-6 break-all" data-testid="wizard-slug-preview">
                  {t("wizard.nahled", "Náhled:")} <span className="text-emerald-300">{previewUrl}</span>
                </p>

                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => finish("save")}
                    disabled={saving || !cleanSlug}
                    data-testid="wizard-save"
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-400 text-[#062019] font-semibold py-3 rounded-xl hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(52,211,153,0.3)]"
                  >
                    <IdCard size={16} /> {t("wizard.nastavitVizitku", "Nastavit vizitku")} <ArrowRight size={14} />
                  </button>
                  <button
                    onClick={() => finish("skip")}
                    disabled={saving}
                    data-testid="wizard-skip"
                    className="px-5 py-3 text-[#A0ABC0] hover:text-white text-sm transition-colors inline-flex items-center justify-center gap-1.5"
                  >
                    <SkipForward size={14} /> {t("wizard.preskocit", "Zatím přeskočit")}
                  </button>
                </div>
                <p className="text-[10px] text-[#64748B] mt-3 leading-relaxed">
                  {t("wizard.pozdejiA", "Pokud teď přeskočíš, můžeš si vizitku nastavit kdykoli v")}{" "}
                  <span className="text-white">{t("wizard.pozdejiKde", "Nastavení → Vizitka")}</span>.{" "}
                  {t("wizard.pozdejiB", "Ale doporučujeme to udělat hned — bez slugu ti nepoběží Leadovač.")}
                </p>
              </>
            ) : (
              <div className="text-center py-6">
                <CheckCircle2 size={42} className="text-emerald-400 mx-auto mb-4" />
                <h2 className="text-white text-xl mb-1" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>
                  {t("wizard.hotovo", "Hotovo!")}
                </h2>
                <p className="text-[#A0ABC0] text-sm">{t("wizard.vrhnemeSe", "Vrhneme se na CRM…")}</p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
